import { Fullpage, Page } from "../../components/Sidebar";
import { useEffect, useRef, useState, useCallback } from "react";
import moment from "moment";
import { LeaveAttendanceApi } from "../../services/LeaveAttendance";
import Pagination from "../../components/Pagination";
import { format } from "date-fns";
import CustomLoader from "../../components/loader";
import { FixedSizeList as List } from "react-window";
import { toast } from "react-toastify";
import Select from "react-select"; // <-- Note: react-select (not @headlessui/react)
import { UsersApi } from "../../services/users";

interface RoleAccess {
    access: string;
}

interface PeopleProps {
    sidebar: boolean;
    sidebarAccess: RoleAccess[];
}
interface FilterData {
    name: string;
    fromDate: string;
    toDate: string;
    status: string;
    type: boolean | ""; // Teaching = true, Non Teaching = false, "" = not selected
    email: string;
}

export default function AttendanceReport(props: PeopleProps) {
    const { getattendanceData, downloadMonthlyAttendance } = LeaveAttendanceApi();
    const { getUserEmail } = UsersApi()
    const currentYear = moment().year();
    const currentMonth = moment().month();
    const currentMonthDay = moment().daysInMonth().toString();

    const initialValues: FilterData = {
        name: '',
        fromDate: moment([currentYear, currentMonth]).format('YYYY-MM-DD'),
        toDate: moment([currentYear, currentMonth, currentMonthDay]).format('YYYY-MM-DD'),
        status: '',
        type: '',
        email: ''
    };

    const [filterData, setFilterData] = useState<any[]>([]);
    const [currentItems, setCurrentItems] = useState<any[]>([]);
    const [attendanceData, setAttendanceData] = useState<any[]>([]);
    const [filterInput, setFilterInput] = useState<FilterData>(initialValues);
    const [userEmailList, setUserEmailList] = useState<any[]>([]);
    const run = useRef(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const filteredData = useCallback(() => {
        const { name, status, fromDate, toDate, email } = filterInput;
        let temp = attendanceData.filter(item => {
            const itemDate = moment(item.date);
            const isWithinDateRange = itemDate.isBetween(fromDate, toDate, undefined, '[]');
            const isNameMatch = item?.name?.toLowerCase().includes(name?.toLowerCase());
            const isStatusMatch = item?.remarks?.toLowerCase().includes(status?.toLowerCase());
            const isEmailMatch = email ? item?.email?.toLowerCase().includes(email?.toLowerCase()) : true;
            return isWithinDateRange && isNameMatch && isStatusMatch && isEmailMatch;
        });

        if (name) {
            temp.sort((a, b) => a.name.localeCompare(b.name));
        } else {
            temp.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        }

        setFilterData(temp);
        setCurrentItems(temp);
    }, [attendanceData, filterInput]);

    useEffect(() => {
        if (run.current) return;
        run.current = true;
        search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        filteredData();
    }, [attendanceData, filterInput, filteredData]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const updatedFilter = { ...filterInput, [name]: value };
        setFilterInput(updatedFilter);

        if (name === 'fromDate' || name === 'toDate') {
            search(updatedFilter);
        }
    };

    const handleTypeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedLabel = e.target.value;

        // Convert string to boolean (or null for default)
        let isTeacher: boolean | "" = "";
        if (selectedLabel === "Teaching") {
            isTeacher = true;
        } else if (selectedLabel === "Non Teaching") {
            isTeacher = false;
        }

        // Update filter state
        setFilterInput(prev => ({
            ...prev,
            type: isTeacher,
            email: '', // Reset email on type change
        }));

        if (selectedLabel) {
            try {
                const response = await getUserEmail(isTeacher); // must expect boolean at backend

                if (Array.isArray(response.data)) {
                    setUserEmailList(response.data);
                } else {
                    toast.error("Invalid email data received.");
                    setUserEmailList([]);
                }
            } catch (err) {
                toast.error("Error fetching email list.");
                console.error(err);
                setUserEmailList([]);
            }
        } else {
            setUserEmailList([]);
        }
    };


    const handleSelectStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;
        setFilterInput({ ...filterInput, [name]: value });
    };

    const search = async (updatedFilterInput?: FilterData) => {
        const { fromDate, toDate } = updatedFilterInput || filterInput;
        setLoading(true);
        setError(null);
        try {
            const response = await getattendanceData(fromDate, toDate);
            console.log("response", response.data.data);

            if (response.data.status === 200) {
                setAttendanceData(response.data.data);
            }
        } catch (error) {
            setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = async () => {
        try {
            setLoading(true);
            const response = await downloadMonthlyAttendance(filterInput.fromDate, filterInput.toDate);
            const blob = new Blob([response], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const formattedFromDate = moment(filterInput.fromDate).format('DD-MM-YYYY');
            const formattedToDate = moment(filterInput.toDate).format('DD-MM-YYYY');
            a.download = `MonthlyAttendance_${formattedFromDate}_to_${formattedToDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Page>
            <Fullpage>
                <div className="w-4/5 min-h-svh mt-5 mb-5 bg-white rounded-xl shadow-lg">
                    {/* <div className="w-full text-center p-6 m-6">
                        <p className="text-center font-bold text-2xl">ATTENDANCE REPORT</p>
                    </div> */}

                    <div className="bg-white shadow-md rounded-xl p-6 mx-6 mb-6">
                        <h2 className="text-xl font-bold mb-4 text-gray-800">Filter Attendance</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
                                <input
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="name"
                                    placeholder="Employee Name"
                                    value={filterInput.name}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-700"
                                    name="status"
                                    value={filterInput.status}
                                    onChange={handleSelectStatusChange}
                                >
                                    <option value="">Select Status</option>
                                    <option value="Absent">Absent</option>
                                    <option value="Present">Present</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Leave">Leave</option>
                                    <option value="Weekend">Weekend</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <select
                                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-700"
                                    name="type"
                                    value={
                                        filterInput.type === true ? "Teaching" :
                                            filterInput.type === false ? "Non Teaching" : ""
                                    }
                                    onChange={handleTypeChange}
                                >
                                    <option value="">Select Type</option>
                                    <option value="Teaching">Teaching</option>
                                    <option value="Non Teaching">Non Teaching</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                                <Select
                                    options={userEmailList.map(email => ({ value: email.email, label: email.email }))}
                                    placeholder="Select Email"
                                    isClearable
                                    value={filterInput.email ? { value: filterInput.email, label: filterInput.email } : null}
                                    onChange={(selectedOption) => {
                                        setFilterInput((prev) => ({
                                            ...prev,
                                            email: selectedOption?.value || '',
                                        }));
                                    }}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: "0.5rem",
                                            borderColor: "#D1D5DB",
                                            boxShadow: "none",
                                            padding: "2px"
                                        }),
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                                <input
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    type="date"
                                    name="fromDate"
                                    value={filterInput.fromDate}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                                <input
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    type="date"
                                    name="toDate"
                                    value={filterInput.toDate}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="md:col-span-3 flex flex-wrap justify-start gap-4 items-center">
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
                                    onClick={downloadExcel}
                                >
                                    Download Monthly Attendance
                                </button>
                                {loading && <CustomLoader size={100} colors="green" />}
                            </div>
                        </div>
                    </div>


                    {loading && <CustomLoader size={150} colors={"green"} />}
                    {error && <div className="text-red-500 text-center mb-5">{error}</div>}

                    <div className='overflow-auto px-5 py-3'>
                        <div className="table-fixed w-full rounded-md shadow-md bg-gradient-to-r from-blue-800 to-indigo-900 overflow-hidden">
                            <div className="thead text-white font-extrabold">
                                <div className="tr flex">
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Employee Name</div>
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Check-In</div>
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Check-Out</div>
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Duration</div>
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Status</div>
                                    <div className="th flex-1 text-center px-4 sm:px-6 py-3">Date</div>
                                </div>
                            </div>
                            <div className="bg-white">
                                {currentItems.length > 0 ? (
                                    <List height={400} itemCount={currentItems.length} itemSize={50} width="100%">
                                        {({ index, style }) => {
                                            const data = currentItems[index];
                                            return (
                                                <div style={style} className="tr flex border-b border-gray-300 font-semibold">
                                                    <div className="td flex-1 text-center p-2">{data.name}</div>
                                                    <div className="td flex-1 text-center p-2">{data.checkIn}</div>
                                                    <div className="td flex-1 text-center p-2">{data.checkOut}</div>
                                                    <div className="td flex-1 text-center p-2">{data.duration}</div>
                                                    <div className="td flex-1 text-center p-2">{data.remarks}</div>
                                                    <div className="td flex-1 text-center p-2">{format(new Date(data.date), 'dd-MM-yyyy')}</div>
                                                </div>
                                            );
                                        }}
                                    </List>
                                ) : (
                                    <div className="tr flex border-b border-gray-300">
                                        <div className="td flex-1 text-center p-2">No data available</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <Pagination isValue={filterData} setCurrentItems={setCurrentItems} />
                    </div>
                </div>
            </Fullpage>
        </Page>
    );
}
